import Button from '@/components/common/Button'
import Icon from '@/components/common/Icon'
import {
  cn,
  Input,
  MinusIcon,
  PlusIcon,
} from 'oddle-shop-monorepo.design-system'
import React, { useCallback } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

interface QuantitySelectorProps {
  name: string
  label?: string
  value?: number
  min?: number
  max?: number
  step?: number
  onChange?: (name: string, value: number) => void
  onInputBlur?: () => void
  onButtonClick?: (name: string, value: number) => void
  disabled?: boolean
  disableIncrease?: boolean
  withInput?: boolean
  iconSize?: 'small' | 'medium' | 'large'
  className?: string
  isButtonIncreaseReversed?: boolean
}

const MinusIconWrapped = Icon(MinusIcon)
const PlusIconWrapped = Icon(PlusIcon)

function useSafeFormContext() {
  try {
    const ctx = useFormContext()
    if (!ctx || !ctx.control) return undefined
    return ctx
  } catch {
    return undefined
  }
}

// Shared UI component
const QuantitySelectorUI: React.FC<{
  currentValue: number
  min: number
  max: number
  label: string
  disabled: boolean
  disableIncrease: boolean
  withInput: boolean
  iconSize: 'small' | 'medium' | 'large'
  className?: string
  onDecrease: () => void
  onIncrease: () => void
  onInputBlur?: () => void
  onInputChange?: (val: number) => void
  isButtonIncreaseReversed?: boolean
}> = ({
  currentValue,
  min,
  max,
  label,
  disabled,
  disableIncrease,
  withInput,
  iconSize,
  className,
  onDecrease,
  onIncrease,
  onInputBlur,
  onInputChange,
  isButtonIncreaseReversed,
}) => {
  const buttonSize = iconSize === 'medium' ? 'h-10 w-10' : 'h-8 w-8'
  const inputSize = iconSize === 'medium' ? 'w-[65px]' : 'w-[45px]'

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Button
        type="button"
        data-testid="button.decrease"
        onClick={onDecrease}
        disabled={disabled || currentValue <= min}
        variant="outline"
        className={cn(
          'text-button border-button bg-container rounded-full border-2 border-solid p-2',
          buttonSize
        )}
      >
        <MinusIconWrapped size={iconSize} />
      </Button>

      {withInput ? (
        <div className={cn('flex h-10 items-center justify-center', inputSize)}>
          <Input
            value={currentValue}
            inputMode="numeric"
            data-testid="quantitySelector.numberInput"
            aria-label={label}
            disabled={disabled || disableIncrease}
            autoFocus={false}
            tabIndex={-1}
            onFocus={(e) => {
              // Make it focusable when user explicitly focuses
              e.target.tabIndex = 0
            }}
            onBlur={(e) => {
              // Remove from tab order when blurred
              e.target.tabIndex = -1
              onInputBlur?.()
            }}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 0
              if (val <= max && onInputChange) {
                onInputChange(val)
              }
            }}
            className="border-input h-10 w-[42px] rounded-md border bg-white px-2 text-center font-bold text-[#515151] disabled:pointer-events-none disabled:opacity-50"
          />
        </div>
      ) : (
        <div className="flex h-10 w-10 items-center justify-center">
          <span className="text-lg font-bold" data-testid="value">
            {currentValue}
          </span>
        </div>
      )}

      <Button
        type="button"
        data-testid="button.increase"
        onClick={onIncrease}
        disabled={disabled || disableIncrease}
        variant="outline"
        className={cn(
          'border-button rounded-full border-2 border-solid p-2',
          buttonSize,
          isButtonIncreaseReversed
            ? 'text-container bg-button'
            : 'text-button bg-container'
        )}
      >
        <PlusIconWrapped size={iconSize} />
      </Button>
    </div>
  )
}

// Standalone version (no form context)
const QuantitySelectorStandalone: React.FC<QuantitySelectorProps> = ({
  name,
  label = 'input',
  value = 0,
  min = 0,
  max = 9999,
  step = 1,
  onChange = () => {},
  onInputBlur = () => {},
  onButtonClick = () => {},
  disabled = false,
  disableIncrease = false,
  withInput = false,
  iconSize = 'medium',
  className,
  isButtonIncreaseReversed,
}) => {
  const [localValue, setLocalValue] = React.useState<number>(value)

  // Keep localValue in sync with value prop
  React.useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleDecrease = useCallback(() => {
    const nextValue = Math.max(localValue - step, min)
    setLocalValue(nextValue)
    onChange(name, nextValue)
    onButtonClick(name, nextValue)
  }, [localValue, step, min, name, onChange, onButtonClick])

  const handleIncrease = useCallback(() => {
    if (disableIncrease) return
    const nextValue = Math.min(localValue + step, max)
    setLocalValue(nextValue)
    onChange(name, nextValue)
    onButtonClick(name, nextValue)
  }, [localValue, step, max, name, onChange, onButtonClick, disableIncrease])

  const handleInputChange = useCallback(
    (val: number) => {
      setLocalValue(val)
      onChange(name, val)
    },
    [name, onChange]
  )

  return (
    <QuantitySelectorUI
      currentValue={localValue}
      min={min}
      max={max}
      label={label}
      disabled={disabled}
      disableIncrease={disableIncrease}
      withInput={withInput}
      iconSize={iconSize}
      className={className}
      onDecrease={handleDecrease}
      onIncrease={handleIncrease}
      onInputBlur={onInputBlur}
      onInputChange={handleInputChange}
      isButtonIncreaseReversed={isButtonIncreaseReversed}
    />
  )
}

// Form version (with form context)
const QuantitySelectorWithForm: React.FC<
  QuantitySelectorProps & { control: any; setValue: any }
> = ({
  name,
  label = 'input',
  value = 0,
  min = 0,
  max = 9999,
  step = 1,
  onChange = () => {},
  onInputBlur = () => {},
  disabled = false,
  disableIncrease = false,
  withInput = false,
  iconSize = 'medium',
  className,
  control,
  setValue,
  isButtonIncreaseReversed,
}) => {
  const watchedValue = useWatch({ control, name, defaultValue: value })

  const handleDecrease = useCallback(() => {
    const nextValue = Math.max(watchedValue - step, min)
    onChange(name, nextValue)
    setValue(name, nextValue)
  }, [watchedValue, step, min, name, onChange, setValue])

  const handleIncrease = useCallback(() => {
    if (disableIncrease) return
    const nextValue = Math.min(watchedValue + step, max)
    onChange(name, nextValue)
    setValue(name, nextValue)
  }, [watchedValue, step, max, name, onChange, setValue, disableIncrease])

  const handleInputChange = useCallback(
    (val: number) => {
      onChange(name, val)
      setValue(name, val)
    },
    [name, onChange, setValue]
  )

  return (
    <QuantitySelectorUI
      currentValue={watchedValue}
      min={min}
      max={max}
      label={label}
      disabled={disabled}
      disableIncrease={disableIncrease}
      withInput={withInput}
      iconSize={iconSize}
      className={className}
      onDecrease={handleDecrease}
      onIncrease={handleIncrease}
      onInputBlur={onInputBlur}
      onInputChange={handleInputChange}
      isButtonIncreaseReversed={isButtonIncreaseReversed}
    />
  )
}

const QuantitySelector: React.FC<QuantitySelectorProps> = (props) => {
  const formContext = useSafeFormContext()
  if (formContext && formContext.control && formContext.setValue) {
    return (
      <QuantitySelectorWithForm
        {...props}
        control={formContext.control}
        setValue={formContext.setValue}
      />
    )
  }
  return <QuantitySelectorStandalone {...props} />
}

export default QuantitySelector
